import React from "react";
import { Calendar, Info } from "lucide-react";
import { EmployeeTimeOffBalanceRow } from "../interfaces/TimeOff";
import { describeBlocking, isUnpaid, notRequestableText } from "../util/timeOffUnpaid";
import "./TimeOffBalancesTable.css";

/** Hours at up to 2dp with trailing zeros trimmed: 36 → "36 h", 3.7 → "3.7 h", 3.73 → "3.73 h".
 *  Time-off hours are entered and modified at 2dp, so rounding to 1dp hid real values. */
export function fmtHrs(h: number): string {
    if (!Number.isFinite(h)) return "0 h";
    return `${Math.round(h * 100) / 100} h`;
}

/** Day conversion on an 8-hour workday: "4.5 days". */
export function fmtDays(h: number): string {
    const d = Math.round((h / 8) * 10) / 10;
    return d === 1 ? "1 day" : `${d} days`;
}

interface TimeOffBalancesTableProps {
    rows: EmployeeTimeOffBalanceRow[];
    /**
     * Category a request in view draws from — its row gets a tint, a left rule and a
     * "This request" tag. Omit outside a request context.
     */
    highlightCategoryID?: number;
    /** Policy attribution line under the table. Omit to leave it out entirely. */
    policyName?: string;
    /** Render the read-only footnote about accruals living in payroll. */
    showReadOnlyNote?: boolean;
    /**
     * In a request context, say why a category can't be requested right now (TAS-586:
     * unpaid time waiting on paid time to be used up). Off on the plain balances view.
     */
    showRequestability?: boolean;
}

/**
 * Per-category time off balances, read-only.
 *
 * Shared by the Employees page (Time Off Policy modal → Balances tab) and the Time Off Approvals
 * page (expanded request row) so the two surfaces read as one concept. Classes are `tobal-`
 * prefixed and unscoped, resolving under either page's root; both declare the same token names.
 *
 * **Available is net.** `currentBalanceHours` already has hours tied up in pending requests
 * deducted (the API debits at submit time), which is why Pending is shown as its own column and
 * must never be subtracted from Available again.
 *
 * **Unpaid Time rows have no balance.** The API sends `currentBalanceHours: 0` on them so old
 * builds don't crash; the row is branched on `isUnpaid` and shows "Unpaid · no balance" instead.
 */
const TimeOffBalancesTable: React.FC<TimeOffBalancesTableProps> = ({
    rows, highlightCategoryID, policyName, showReadOnlyNote = true, showRequestability = false,
}) => (
    <>
        <div className="tobal-head">
            <span>Category</span>
            <span>Available</span>
            <span>Accrued YTD</span>
            <span>Used YTD</span>
            <span>Pending</span>
        </div>
        <div className="tobal-tbl">
            {rows.map((row) => {
                const isThis = highlightCategoryID != null && row.timeOffCategoryID === highlightCategoryID;
                const notRequestable = showRequestability ? notRequestableText(row) : null;
                if (isUnpaid(row)) {
                    return (
                        <div className={`tobal-row${isThis ? " tobal-this" : ""}`} key={row.timeOffCategoryID}>
                            <div className="tobal-cat">
                                {row.categoryName}
                                {isThis && <span className="tobal-tag">This request</span>}
                                {notRequestable && (
                                    <span
                                        className="tobal-blocked"
                                        title={(row.blocking ?? []).map(describeBlocking).join("\n") || undefined}
                                    >
                                        {notRequestable}
                                    </span>
                                )}
                            </div>
                            <div className="tobal-unpaid">Unpaid · no balance</div>
                        </div>
                    );
                }
                return (
                    <div className={`tobal-row${isThis ? " tobal-this" : ""}`} key={row.timeOffCategoryID}>
                        <div className="tobal-cat">
                            {row.categoryName}
                            {isThis && <span className="tobal-tag">This request</span>}
                            {notRequestable && <span className="tobal-blocked">{notRequestable}</span>}
                        </div>
                        <div className="tobal-num accent">
                            {fmtHrs(row.currentBalanceHours)}
                            <span className="tobal-days">{fmtDays(row.currentBalanceHours)}</span>
                        </div>
                        <div className="tobal-num">{fmtHrs(row.accruedYTDHours)}</div>
                        <div className="tobal-num">{fmtHrs(row.usedYTDHours)}</div>
                        <div
                            className={`tobal-num${row.pendingHours > 0 ? " warn" : ""}`}
                            title={
                                row.pendingHours > 0 &&
                                row.pendingApprovalHours !== undefined &&
                                row.awaitingRecordingHours !== undefined
                                    ? `${fmtHrs(row.pendingApprovalHours)} awaiting approval · ${fmtHrs(row.awaitingRecordingHours)} approved, awaiting payroll recording`
                                    : undefined
                            }
                        >
                            {fmtHrs(row.pendingHours)}
                        </div>
                    </div>
                );
            })}
        </div>
        {policyName && (
            <div className="tobal-meta">
                <Calendar size={16} />
                <div>
                    Under <b>{policyName}</b>. Each category accrues and is capped independently.
                </div>
            </div>
        )}
        {showReadOnlyNote && (
            <div className="tobal-ro-note">
                <Info size={16} />
                Balances are as of today and read-only here. Accruals and adjustments are managed in payroll.
            </div>
        )}
    </>
);

export default TimeOffBalancesTable;
